import type { DomainError } from './domain-error';

/**
 * Resultado de uma operação que pode falhar por regra de negócio.
 *
 * Erro **esperado** (capacidade excedida, justificativa ausente, CPF inválido) é devolvido
 * como valor — não lançado. Exceção fica reservada para o que é genuinamente inesperado.
 * Ver docs/PLANO_GERAL.md §4.3.
 */
export type Result<T, E = DomainError> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: E };

export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
}

export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

export function isOk<T, E>(result: Result<T, E>): result is { ok: true; value: T } {
  return result.ok;
}

export function isErr<T, E>(result: Result<T, E>): result is { ok: false; error: E } {
  return !result.ok;
}

/** Extrai o valor. Só use quando a falha for genuinamente impossível (ex.: literal validado). */
export function unwrap<T, E>(result: Result<T, E>): T {
  if (!result.ok) {
    throw new Error(
      `unwrap() em Result de erro: ${JSON.stringify(result.error)}. ` +
        'Trate o erro explicitamente em vez de desembrulhar.',
    );
  }
  return result.value;
}

export function unwrapOr<T, E>(result: Result<T, E>, fallback: T): T {
  return result.ok ? result.value : fallback;
}

export function map<T, U, E>(result: Result<T, E>, fn: (value: T) => U): Result<U, E> {
  return result.ok ? ok(fn(result.value)) : result;
}

export function andThen<T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E>,
): Result<U, E> {
  return result.ok ? fn(result.value) : result;
}

/** Converte uma lista de Results em um Result de lista, parando no primeiro erro. */
export function collect<T, E>(results: readonly Result<T, E>[]): Result<T[], E> {
  const values: T[] = [];
  for (const result of results) {
    if (!result.ok) return result;
    values.push(result.value);
  }
  return ok(values);
}
